import React, { useRef, useState } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ComputerRoundedIcon from '@mui/icons-material/ComputerRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';


const VIEWPORT_WIDTHS = Object.freeze({
  desktop: '100%',
  mobile: '390px',
});

const PreviewFrame = ({
  children,
  viewport,
  fullscreen = false,
  mobileContext = false,
}) => (
  <Box
    data-testid={fullscreen
      ? 'salon-theme-fullscreen-preview-viewport'
      : 'salon-theme-preview-viewport'}
    data-viewport={viewport}
    style={{
      width: fullscreen || mobileContext ? '100%' : VIEWPORT_WIDTHS[viewport],
      height: fullscreen ? 'calc(100vh - 64px)' : 'auto',
      maxHeight: fullscreen
        ? 'none'
        : (mobileContext ? 'none' : 'calc(100vh - 336px)'),
      overflow: 'auto',
    }}
    sx={{
      maxWidth: '100%',
      mx: 'auto',
      transition: fullscreen ? undefined : 'width 180ms ease',
      flex: fullscreen ? undefined : 1,
      minHeight: 0,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Box
      data-testid={fullscreen
        ? 'salon-theme-fullscreen-preview-canvas'
        : 'salon-theme-preview-canvas'}
      style={{
        width: '100%',
        minWidth: '0',
      }}
      sx={{ minHeight: '100%' }}
    >
      {children}
    </Box>
  </Box>
);

const SalonThemeViewportToggle = ({ children, mobileContext = false }) => {
  const { t } = useTranslation();
  const [viewport, setViewport] = useState('desktop');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const fullscreenOpenerRef = useRef(null);

  const activeViewport = mobileContext ? 'mobile' : viewport;

  const handleViewportChange = (_event, nextViewport) => {
    if (nextViewport) setViewport(nextViewport);
  };

  const closeFullscreen = () => {
    setFullscreenOpen(false);
    fullscreenOpenerRef.current?.focus();
  };

  const renderPreview = () => (
    typeof children === 'function' ? children(activeViewport) : children
  );

  return (
    <Stack
      data-testid="salon-theme-preview-shell"
      spacing={1.5}
    >
      {!mobileContext && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={activeViewport}
            onChange={handleViewportChange}
            aria-label={t('salon_theme.preview.viewport_label')}
          >
            <ToggleButton value="desktop" aria-label={t('salon_theme.preview.viewport_desktop')}>
              <ComputerRoundedIcon fontSize="small" sx={{ me: 0.75 }} />
              {t('salon_theme.preview.viewport_desktop')}
            </ToggleButton>
            <ToggleButton value="mobile" aria-label={t('salon_theme.preview.viewport_mobile')}>
              <PhoneIphoneRoundedIcon fontSize="small" sx={{ me: 0.75 }} />
              {t('salon_theme.preview.viewport_mobile')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            ref={fullscreenOpenerRef}
            type="button"
            size="small"
            startIcon={<OpenInFullRoundedIcon />}
            onClick={() => setFullscreenOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            {t('salon_theme.preview.open_fullscreen')}
          </Button>
        </Stack>
      )}

      <PreviewFrame viewport={activeViewport} mobileContext={mobileContext}>
        {renderPreview()}
      </PreviewFrame>

      <Dialog
        fullScreen
        open={fullscreenOpen}
        onClose={closeFullscreen}
        aria-labelledby="salon-theme-fullscreen-title"
      >
        <AppBar position="sticky" color="default" elevation={0}>
          <Toolbar>
            <Typography
              id="salon-theme-fullscreen-title"
              component="h2"
              variant="h6"
              sx={{ flex: 1 }}
            >
              {t('salon_theme.preview.fullscreen_title')}
            </Typography>
            <IconButton
              edge="end"
              aria-label={t('salon_theme.preview.close_fullscreen')}
              onClick={closeFullscreen}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <PreviewFrame viewport={activeViewport} fullscreen mobileContext={mobileContext}>
          {renderPreview()}
        </PreviewFrame>
      </Dialog>
    </Stack>
  );
};

export default SalonThemeViewportToggle;
